// tokenService.js
// Central place for access-token validity checks and refreshing.
//
// The refresh token now lives in an HttpOnly cookie set by the server, so JavaScript
// never touches it — this is what lets the session survive iOS Safari's ITP 7-day cap
// on script-written cookies. All refreshing goes through refreshAccessToken(), which is
// single-flight: concurrent callers (the axios interceptor + the router guard) share one
// in-flight request instead of each firing their own.
import axios from 'axios';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import config from '../config';

const ACCESS_TOKEN = 'accessToken';
const LEGACY_REFRESH_TOKEN = 'refreshToken';

export function getAccessToken() {
  return Cookies.get(ACCESS_TOKEN);
}

// True only if the token exists and its exp is still in the future.
export function isTokenValid(token) {
  if (!token) return false;
  try {
    const { exp } = jwtDecode(token);
    return typeof exp === 'number' && exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

let refreshPromise = null;

// Latched by clearSession(). Without it, logging out races the async server-side
// Logout call: the router guard's checkAuth() sees no access token, refreshes via
// the still-valid HttpOnly refresh cookie, re-mints an access token and bounces
// the user straight back into the app. Reset on successful login (and by any
// full page reload, by which time the server has dropped the refresh cookie).
let loggedOut = false;

export function markSessionActive() {
  loggedOut = false;
}

// Single-flight refresh. Resolves to a fresh access token, or null on failure.
export function refreshAccessToken() {
  if (loggedOut) return Promise.resolve(null);
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      // Empty body: the server reads the refresh token from the HttpOnly cookie.
      // withCredentials ensures the cookie is sent/stored even if the API ever
      // moves to a different subdomain.
      const response = await axios.post(
        `${config.apiUrl}/identity/RefreshToken`,
        {},
        { withCredentials: true }
      );
      const token = response.data.accessToken;
      Cookies.set(ACCESS_TOKEN, token, { secure: config.secureCookies, expires: 7 });
      // Remove the pre-HttpOnly refresh token if it is still lingering from an old session.
      Cookies.remove(LEGACY_REFRESH_TOKEN);
      return token;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Returns a valid access token, refreshing if the current one is missing/expired.
export async function ensureValidToken() {
  const token = getAccessToken();
  if (isTokenValid(token)) return token;
  return refreshAccessToken();
}

// Clears all client-side auth state and asks the server to drop the HttpOnly refresh cookie.
export function clearSession() {
  loggedOut = true;
  Cookies.remove(ACCESS_TOKEN);
  Cookies.remove(LEGACY_REFRESH_TOKEN);
  // Best-effort: JS cannot delete the HttpOnly cookie, only the server can.
  axios.post(`${config.apiUrl}/identity/Logout`, {}, { withCredentials: true }).catch(() => {});
}
